import type { TalkingPoint } from "@acto/genui-components-schema";
import type { useTranslations } from "@acto/web-common/locales";

type Translate = ReturnType<typeof useTranslations>;

/** The card keys the producer stamps, one per analytics signal. */
export const NAMED_CARD_KEYS = [
  "account_revenue_yoy",
  "order_frequency",
  "last_contact",
  "declining_products",
  "growing_products",
  "inactive_products",
  "expected_repurchase",
  "recommended_products",
  "ranked_offers",
  "open_todos",
] as const;

export type NamedCardKey = (typeof NAMED_CARD_KEYS)[number];

const NAMED = new Set<string>(NAMED_CARD_KEYS);

export function isNamedCardKey(cardKey: string): cardKey is NamedCardKey {
  return NAMED.has(cardKey);
}

export function cardNameLabel(cardKey: NamedCardKey, t: Translate): string {
  return t(`cardName.${cardKey}`);
}

type CardHideLocale<Locale> = Locale extends {
  fieldSalesTwo: { genuiMeetingPrep: { cardHide: infer CardHide } };
}
  ? CardHide
  : never;

type NamesEvery<Keys extends string, Names extends Record<Keys, string>> = Names;

// Fails the type check when a card type or named key lacks a label in either locale.
export type CardLabelLocaleCoverage = [
  NamesEvery<TalkingPoint["type"], CardHideLocale<typeof import("@acto/taskpane/core/locales/de-DE.json")>["cardType"]>,
  NamesEvery<TalkingPoint["type"], CardHideLocale<typeof import("@acto/taskpane/core/locales/en-US.json")>["cardType"]>,
  NamesEvery<NamedCardKey, CardHideLocale<typeof import("@acto/taskpane/core/locales/de-DE.json")>["cardName"]>,
  NamesEvery<NamedCardKey, CardHideLocale<typeof import("@acto/taskpane/core/locales/en-US.json")>["cardName"]>,
];

export interface LabelledCard {
  type: TalkingPoint["type"];
  cardKey?: string | null;
}

/**
 * Short name of the signal a card renders. Never the card's own prose — a headline or metric label
 * is customer-specific text, not a name for the card.
 */
export function talkingPointLabel(card: LabelledCard, t: Translate): string {
  if (card.cardKey && isNamedCardKey(card.cardKey)) {
    return cardNameLabel(card.cardKey, t);
  }
  return t(`cardType.${card.type}`);
}
